import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { TaskDateFormatter } from '@/services/date-format/task-date-formatter';
import { Emitter, Logger } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import _ from 'lodash';
import { FtoReportTitleApi } from '../../apis/novelty-check-report-title-api';
import { FtoReportSubtitleInfoApi } from '../../apis/novelty-report-subtitle-info-api';
import { FtoReportTechEssentialUpdateApi } from '../../apis/tech-essential-update-api';
import { FtoReportRetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { FtoReportTaskStatusModel } from '../../models/task-status-model';
import { FtoReportFeatureIdentify, type FtoReportFeatureRecognizeResult } from '../feature-identify';
import { FtoReportComparativeLiteraturePublicityService, FtoReportNoveltyReviewService, FtoReportOriginalInputService, FtoReportRetrievalResultReportSubtitleInfoService, FtoReportTechEssentialService, FtoReportTechFeaturesService, FtoReportTitleService } from '../retrieval-result-report-service';


@injectable()
export class FtoReportRetrievalResultReportServiceImpl implements
    FtoReportComparativeLiteraturePublicityService,
    FtoReportRetrievalResultReportSubtitleInfoService,
    FtoReportTechEssentialService,
    FtoReportTechFeaturesService,
    FtoReportOriginalInputService,
    FtoReportTitleService,
    FtoReportNoveltyReviewService {
    // eslint-disable-next-line max-params
    constructor(
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
        @inject(FtoReportTitleApi)
        private readonly _noveltyCheckReportTitleApi: FtoReportTitleApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FtoReportTechEssentialUpdateApi)
        private readonly _techSummaryUpdateApi: FtoReportTechEssentialUpdateApi,
        @inject(FtoReportSubtitleInfoApi)
        private readonly _noveltyReportSubtitleInfoApi: FtoReportSubtitleInfoApi,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportFeatureIdentify)
        private readonly _featureIdentify: FtoReportFeatureIdentify,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
        @inject(TaskDateFormatter)
        private readonly _taskDateFormatter: TaskDateFormatter,
    ) { }



    private _onComparativeLiteraturePublicityLoadingEmitter = new Emitter<boolean>();

    get onComparativeLiteraturePublicityLoading() {
        return this._onComparativeLiteraturePublicityLoadingEmitter.event;
    }

    pullRetrievalResultReportSubtitleInfo(): Promise<void> {
        return this._noveltyReportSubtitleInfoRepository();
    }

    private async _noveltyReportSubtitleInfoRepository(): Promise<void> {
        const res = await this._noveltyReportSubtitleInfoApi.getNoveltyReportSubtitleInfo({
            task_id: this.taskId,
        });
        this._retrievalResultReportModel.update((state) => {
            state.creator = res.creator;
            state.generatedTime = res.generated_time;
            state.searchQty = res.search_qty;
            state.comparativeQty = res.comparative_qty;
            state.nearestQty = res.nearest_qty;
            state.secondaryApproachQty = res.secondary_approach_qty;
            state.searchScope = res.search_scope;
            state.header = res.header;
            return state;
        });
        this._noveltyCheckTaskStatusModel.update((state) => {
            state.lastUpdateTime = this._taskDateFormatter.format(res.generated_time);
            return state;
        });
    }

    async pullTechEssential(): Promise<string> {
        const data = await this.pullTechFeatures();
        return data.text;
    }

    async updateTechEssential(techEssential: string): Promise<void> {
        // 先客户端更新
        this._retrievalResultReportModel.update((state) => {
            state.techEssential = techEssential;
            return state;
        });
        // 再服务端更新
        await this._techSummaryUpdateApi.updateTechSummary({
            task_id: this.taskId,
            text: techEssential,
        });
    }


    private _onNoveltyReviewLoadingEmitter = new Emitter<boolean>();

    get onNoveltyReviewLoading() {
        return this._onNoveltyReviewLoadingEmitter.event;
    }


    async pullNoveltyCheckReportTitle(): Promise<string> {
        const title = await this._getNoveltyCheckReportTitleRepository();
        this._retrievalResultReportModel.update((state) => {
            state.title = title;
            return state;
        });
        return title;
    }

    private async _getNoveltyCheckReportTitleRepository(): Promise<string> {
        const title = await this._noveltyCheckReportTitleApi.getNoveltyCheckReportTitle({
            task_id: this.taskId,
        });
        return title;
    }

    pullOriginalInput(): Promise<string> {
        this._logger.info('FtoReportRetrievalResultReportServiceImpl.pullOriginalInput unimplemented');
        return Promise.resolve('');
    }

    private get taskId() {
        return this._currentTaskService.taskId;
    }

    pullTechFeatures(): Promise<FtoReportFeatureRecognizeResult> {
        return this._featureIdentify.recognize(this.taskId);
    }
}
